import { useEffect, useRef, useState } from 'react';

interface Props {
  mobile: string | null;
  desktop: string | null;
  alt: string;
  dia: string;
  mes: string;
  /** Carrega sem esperar rolagem (primeiros cartões da tela). */
  carregarJa?: boolean;
  /** Escala do dia/mês quando não há imagem. */
  tamanho?: 'pequeno' | 'grande';
  /**
   * cover: preenche o quadro cortando as sobras (bom para miniatura).
   * conter: mostra a arte inteira sobre um fundo desfocado da própria arte.
   * natural: a caixa segue o formato real da arte (img w-full h-auto), com
   *   teto de 75vh. O fundo desfocado só entra quando a arte passa desse teto.
   *   Antes de carregar, reserva 4:5 com fundo cinza.
   */
  ajuste?: 'cover' | 'conter' | 'natural';
}

const TETO_VH = 0.75;

/**
 * Cartaz do evento com rede de segurança:
 * 1. tenta a imagem do celular;
 * 2. se falhar, tenta a do computador;
 * 3. se as duas falharem (ou não existirem), mostra um fundo suave com o
 *    dia/mês grande — nunca um quadrado chapado.
 */
export default function CartazEvento({
  mobile,
  desktop,
  alt,
  dia,
  mes,
  carregarJa = false,
  tamanho = 'pequeno',
  ajuste = 'cover',
}: Props) {
  const fontes = [mobile, desktop].filter(
    (v, i, arr): v is string => !!v && arr.indexOf(v) === i,
  );
  const chave = fontes.join('|');
  const [indice, setIndice] = useState(0);
  // Guardamos a chave junto do estado: quando o cartaz muda, o "carregou"
  // cai sozinho (sem efeito de reset que possa apagar um load já disparado).
  const [carregadoDe, setCarregadoDe] = useState<string | null>(null);
  const [tetoDe, setTetoDe] = useState<string | null>(null);
  const [falhouDe, setFalhouDe] = useState<string | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  // Troca de evento/capa: volta para a primeira fonte.
  useEffect(() => {
    setIndice(0);
  }, [chave]);

  // Se a segunda fonte também falhar, cai no plano B.
  const src = indice < fontes.length ? fontes[indice] : null;

  if (!src) {
    return (
      <div
        className={
          ajuste === 'natural'
            ? 'aspect-video w-full bg-background-200'
            : 'h-full w-full'
        }
      >
        <div
          className="flex h-full w-full flex-col items-center justify-center bg-gradient-to-br from-background-100 via-background-50 to-primary-100 text-primary-700"
          aria-hidden
        >
          <span
            className={`font-extrabold leading-none ${
              tamanho === 'grande' ? 'text-[56px]' : 'text-[26px]'
            }`}
          >
            {dia || '—'}
          </span>
          {mes && (
            <span
              className={`mt-1 font-bold uppercase tracking-[0.14em] ${
                tamanho === 'grande' ? 'text-[13px]' : 'text-[10px]'
              }`}
            >
              {mes}
            </span>
          )}
          <i
            className={`ri-image-line text-primary-300 ${
              tamanho === 'grande' ? 'mt-3 text-[20px]' : 'mt-1.5 text-[13px]'
            }`}
            aria-hidden
          />
        </div>
      </div>
    );
  }

  const natural = ajuste === 'natural';
  const carregou = carregadoDe === chave;
  const passouDoTeto = tetoDe === chave;
  const falhou = falhouDe === chave;
  const reservando = natural && !carregou && !falhou;

  const medir = (el: HTMLImageElement) => {
    if (!el.naturalWidth) return;
    const alturaNatural = el.clientWidth * (el.naturalHeight / el.naturalWidth);
    setTetoDe(alturaNatural > window.innerHeight * TETO_VH + 1 ? chave : null);
  };

  const concluirCarregamento = (el: HTMLImageElement) => {
    setCarregadoDe(chave);
    medir(el);
  };

  const imagem = (
    <img
      ref={(el) => {
        imgRef.current = el;
        if (el && el.complete && el.naturalWidth > 0) concluirCarregamento(el);
      }}
      src={src}
      alt={alt}
      title={alt}
      onError={() => setFalhouDe(chave)}
      onLoad={(event) => concluirCarregamento(event.currentTarget)}
      loading={carregarJa ? 'eager' : 'lazy'}
      decoding="async"
      className={`transition-opacity duration-200 ease-out motion-reduce:transition-none ${
        carregou ? 'opacity-100' : 'opacity-0'
      } ${
        natural
          ? 'relative block h-auto w-full max-h-[75vh] object-contain object-center'
          : ajuste === 'conter'
            ? 'relative h-full w-full object-contain object-center'
            : 'h-full w-full object-cover object-top'
      }`}
    />
  );

  // Caixa acompanhando o formato real do cartaz (teto de 75vh). Antes de
  // carregar, reserva 4:5 num fundo cinza para a tela não pular; depois de
  // carregar a classe de proporção SAI e a altura passa a ser a da arte.
  if (natural) {
    // Erro ao carregar: mantém o placeholder cinza.
    if (falhou) {
      return (
        <div className="relative aspect-video w-full overflow-hidden bg-background-200" aria-hidden>
          <div className="absolute inset-0 flex items-center justify-center bg-background-200">
            <i className="ri-image-line text-[22px] text-foreground-400" aria-hidden />
          </div>
        </div>
      );
    }

    return (
      <div
        className={`relative w-full overflow-hidden bg-background-200 ${
          reservando ? 'aspect-video' : ''
        }`}
      >
        {/* fundo desfocado: só quando a arte foi reduzida para caber em 75vh */}
        {passouDoTeto && (
          <img
            src={src}
            alt=""
            aria-hidden
            className="absolute inset-0 h-full w-full scale-110 object-cover object-center blur-xl brightness-[0.55]"
            loading={carregarJa ? 'eager' : 'lazy'}
            decoding="async"
          />
        )}
        {imagem}
      </div>
    );
  }

  // Arte inteira: a mesma imagem vai atrás, desfocada e escurecida, para
  // preencher as sobras sem cortar o texto do flyer.
  if (ajuste === 'conter') {
    return (
      <div className="relative h-full w-full overflow-hidden bg-foreground-950">
        <img
          src={src}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full scale-110 object-cover object-center blur-xl brightness-[0.55]"
          loading={carregarJa ? 'eager' : 'lazy'}
          decoding="async"
        />
        {imagem}
      </div>
    );
  }

  return imagem;
}